import { useMemo } from 'react';

import { ROLES } from '../../../_configuration';
import { getCurrMember } from './member.service';
import { getAssessments } from './assessment.service';
import { getActiveCompanyId, getCompany } from './company.service';
import { providedMemberHasRoleInAnyScope } from './user.service';

// Hook (wraps getCurrMember/getAssessments — a "hook in disguise"): mirrors the
// SCORING check RouteByRole runs. Returns `loading` to avoid a flash-redirect.
export const hasScoringAccess = (): { hasScoring: boolean; loading: boolean } => {
  const { member, loading: memberLoading } = getCurrMember();
  const { assessments, loading: assessmentsLoading } = getAssessments();

  // `loading` stays true only while initial data is missing, so a warm-cache refetch
  // doesn't flash a spinner / flash-redirect (both start empty under cache-and-network).

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(() => ({
    hasScoring: providedMemberHasRoleInAnyScope({ member, assessments, roles: [ROLES.SCORING] }),
    loading: (memberLoading && !member) || (assessmentsLoading && !assessments.length),
  }), [member, assessments, memberLoading, assessmentsLoading]);
};

// Calibration Assignments need SCORING (any scope) AND the company opt-in flag. Hook
// (composes hasScoringAccess + getCompany); reads company so `loading` covers cold deep-links.
export const hasCalibrationAssignmentAccess = (): { hasAccess: boolean; loading: boolean } => {
  const { hasScoring, loading: scoringLoading } = hasScoringAccess();
  const { company, loading: companyLoading } = getCompany(getActiveCompanyId());

  return {
    hasAccess: hasScoring && !!company?.hasCalibrationVideos,
    loading: scoringLoading || (companyLoading && !company),
  };
};
